import { Link } from "wouter";
import { PageHeader } from "@/components/layout/page-header";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { StatusBadge } from "@/components/data/status-badge";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { useVisaoGeral } from "@/hooks/use-plataforma";
import { origemAtual } from "@/lib/navegacao";
import { ROTULO_STATUS_ORG, type StatusOrg } from "@shared/org-status";

/**
 * Plataforma › Visão geral — os números do Rizoma inteiro, em StatCards.
 *
 * Cada card responde UMA pergunta e leva à lista de Fazendas já filtrada: o
 * número sem o caminho para quem é o número seria decoração.
 */
export default function PlataformaVisaoGeral() {
  const consulta = useVisaoGeral();
  const aguardando = estadoDaConsulta(consulta, { variante: "cards", linhas: 4 });
  if (aguardando) return <div className="space-y-6"><PageHeader titulo="Plataforma" descricao="Fazendas e praça, na visão do Rizoma" />{aguardando}</div>;
  const v = consulta.data!;

  const ordem: StatusOrg[] = ["TRIAL", "TRIAL_EXPIRADO", "ASSINANTE", "CORTESIA", "INATIVA"];

  return (
    <div className="space-y-6">
      <PageHeader titulo="Plataforma" descricao="Fazendas e praça, na visão do Rizoma" />

      <StatCardGrid>
        <StatCard
          rotulo="Fazendas"
          valor={String(v.fazendas.total)}
          legenda={
            <span>
              {v.fazendas.porTipo.PRODUTOR ?? 0} produtoras · {v.fazendas.porTipo.COMPRADOR ?? 0} compradoras
            </span>
          }
          testId="stat-fazendas"
        />
        <StatCard
          rotulo="Trials vencendo"
          valor={String(v.trialsVencendo7d)}
          unidade="em 7 dias"
          legenda={v.trialsVencendo7d > 0
            ? <StatusBadge tom="atencao" ponto>precisa de decisão</StatusBadge>
            : <span>nenhum vence esta semana</span>}
          testId="stat-trials-vencendo"
        />
        <StatCard
          rotulo="Sem acesso"
          valor={String(v.semAcesso30d)}
          unidade="há 30 dias"
          legenda={<span>ativas em que ninguém entrou no último mês</span>}
          testId="stat-sem-acesso"
        />
        <StatCard
          rotulo="Corretores ativos"
          valor={String(v.corretoresAtivos)}
          legenda={<span>compradores com pelo menos um login ativo</span>}
          testId="stat-corretores"
        />
      </StatCardGrid>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Por status</h2>
        <StatCardGrid>
          {ordem.map(s => (
            <Link key={s} href={`/plataforma/fazendas?status=${s}`} state={origemAtual()} className="block">
              <StatCard
                rotulo={ROTULO_STATUS_ORG[s]}
                valor={String(v.fazendas.porStatus[s] ?? 0)}
                legenda={<span className="underline-offset-2 hover:underline">ver fazendas</span>}
                testId={`stat-status-${s}`}
              />
            </Link>
          ))}
        </StatCardGrid>
      </section>

      {v.acessosPendentes > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="acessos-pendentes">
          {v.acessosPendentes} pedido(s) de acesso de suporte aguardando o admin da fazenda.
        </p>
      )}
    </div>
  );
}
